/*
	* Klaviyo's full-screen signup popup is named "FULLSCREEN Form", and when it opens
	* Klaviyo calls focus() on its first control: the Close button, or on mobile the
	* email field, which sits after the heading and the interest checkboxes. VoiceOver
	* lands there without announcing what opened.
	*
	* We can't change Klaviyo's code, but the popup is in the page DOM: name the dialog
	* from its visible heading, and once the popup has faded in, focus that heading.
	* Focusing the dialog container itself doesn't work on iOS: VoiceOver doesn't read
	* the container and moves straight on to its first control, Close. Close comes
	* first in reading order, so it is one swipe back from the heading.
	*
	* Until then the popup's controls are held inert. focus() on an inert element does
	* nothing, so Klaviyo's own focus call can't start VoiceOver reading the email field
	* only for our heading focus to cut it off. Handled once per dialog instance, and a
	* tap or key press inside the popup releases the controls straight away.
*/
if ( typeof window.enhancedKlaviyoPopups === 'undefined' ) {

	window.enhancedKlaviyoPopups = new WeakSet();

	// Klaviyo calls focus() about 200ms after inserting the popup, and fades it in over
	// 350ms. Focus the heading after both, so it is visible and nothing follows it.
	const KLAVIYO_SETTLE_DELAY = 600;

	const KLAVIYO_CONTROLS = 'button, input, select, textarea, a[href], [tabindex]:not([role="heading"])';

	// Controls each popup is holding inert, until its heading takes focus.
	const heldControls = new WeakMap();

	function setAttributeIfChanged(elm, name, value){
		// The caller runs from a MutationObserver; rewriting an identical value is
		// wasted work on every Klaviyo re-render.
		if ( elm.getAttribute(name) !== value ) elm.setAttribute(name, value);
	}

	// Klaviyo keeps rendering controls after its first pass, so the caller runs this
	// on every mutation to hold each new one as it appears.
	function holdNewControls(dialog){
		const held = heldControls.get(dialog);
		if ( ! held ) return;
		dialog.querySelectorAll(KLAVIYO_CONTROLS).forEach(elm=>{
			if ( ! held.has(elm) && ! elm.hasAttribute('inert') ) {
				held.add(elm);
				elm.setAttribute('inert', '');
			}
		});
	}

	function findHeading(dialog){
		return [...dialog.querySelectorAll('.klaviyo-form-richtext')].find(elm=>elm.textContent.trim());
	}

	/*
		* Klaviyo renders with Preact, which can replace the heading element on
		* re-render (for example between form steps), so the naming is idempotent and
		* re-applied on every mutation.
	*/
	function nameKlaviyoPopup(dialog){

		// The dialog mounts before its content; the caller's observer brings us back
		// here until the heading exists.
		const heading = findHeading(dialog);
		if ( ! heading ) return;

		if ( ! heading.id ) heading.id = `klaviyo-popup-heading-${Math.random().toString(36).slice(2, 8)}`;
		setAttributeIfChanged(heading, 'role', 'heading');
		setAttributeIfChanged(heading, 'aria-level', '2');
		setAttributeIfChanged(heading, 'tabindex', '-1');
		setAttributeIfChanged(dialog, 'aria-labelledby', heading.id);
		// aria-labelledby already outranks it, but don't leave the generic name
		// for anything that reads aria-label first.
		if ( dialog.hasAttribute('aria-label') ) dialog.removeAttribute('aria-label');

	}

	function enhanceKlaviyoPopup(dialog){

		nameKlaviyoPopup(dialog);

		if ( window.enhancedKlaviyoPopups.has(dialog) ) {
			holdNewControls(dialog);
			return;
		}
		window.enhancedKlaviyoPopups.add(dialog);

		// The observer calls this in the same task that inserted the popup, before
		// Klaviyo's focus call runs, so the controls are already inert when it does.
		heldControls.set(dialog, new Set());
		holdNewControls(dialog);

		let userInteracted = false;
		const release = ()=>{
			const controls = heldControls.get(dialog);
			if ( ! controls ) return;
			heldControls.delete(dialog);
			controls.forEach(elm=>elm.removeAttribute('inert'));
		};
		const onInteract = ()=>{ userInteracted = true; release(); };
		['pointerdown', 'touchstart', 'keydown'].forEach(type=>dialog.addEventListener(type, onInteract, {capture: true, once: true}));

		setTimeout(()=>{
			release();
			// Look the heading up again: a re-render may have replaced it.
			const current = findHeading(dialog);
			if ( userInteracted || ! dialog.isConnected || ! current ) return;
			current.focus({preventScroll: true});
		}, KLAVIYO_SETTLE_DELAY);

	}

	function findKlaviyoPopups(){
		document.querySelectorAll('div[role="dialog"][aria-modal="true"]').forEach(dialog=>{
			if ( dialog.querySelector('form.klaviyo-form') ) enhanceKlaviyoPopup(dialog);
		});
	}

	new MutationObserver(findKlaviyoPopups).observe(document.body, {childList: true, subtree: true});
	findKlaviyoPopups();

}
